import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageMeta from '../components/PageMeta';
import { priceLabel, priceAmount, TRIAL_DAYS, INCLUDED } from '../marketing/pricing';
import { useReveal } from './Landing';
import './Landing.css';

// Sub-page for YMCA basketball coaches (2026-09-13). Same design system as
// Landing.js (same CSS, nav and footer), written for a coach running a
// Free (YMCA) program: nothing on this page mentions fees, payments or
// balances, because a Free (YMCA) program never sees any of that in the
// app. The main landing page is unchanged apart from one link here.
//
// Rules this file keeps, same as Landing.js: react-snap prerender safety
// (never `text{' '}<a>`, build mixed strings with template literals), the
// price comes only from marketing/pricing.js, and the FAQ list below is the
// single source for both the visible FAQ and the FAQPage JSON-LD.
//
// Name only, no YMCA marks or logos (v2.0 Risk R3).

const PATH = '/ymca-basketball';
const REGISTER_YMCA = '/coach/register?type=free_ymca';

const FAQS = [
  {
    q: 'Is HoopHero affiliated with the YMCA?',
    a: 'No. HoopHero is independent software and is not affiliated with or endorsed by the YMCA. Choosing Free (YMCA) at sign-up only tells the app which branch hosts your program, so your team is easy for parents to recognize.',
  },
  {
    q: 'What changes when I pick Free (YMCA)?',
    a: 'The app drops everything about money. There is no fee field, no Payments page and nothing about a price in front of your parents, coach side or parent side. Your YMCA branch is shown in your header and in every parent’s portal. Everything else, rosters, schedule, RSVP, attendance, messaging and reminders, works the same as any other program.',
  },
  {
    q: 'My branch is not in the directory. Can I still sign up?',
    a: 'Yes. Pick your state, and if your branch is not listed, choose “not listed” and type its name. The name you type is what your parents will see.',
  },
  {
    q: 'What do parents see?',
    a: 'Parents join with your program code or join link, add their own children, see the schedule, RSVP in one tap per child, sign the waiver, and reply to you in the app or by email. They see your YMCA branch at the top of their portal and nothing about money anywhere.',
  },
  {
    q: 'Do parents get text messages?',
    a: 'Text messages are coming soon, once the carriers approve our texting program. Until then families get every update by email and as a notification on phones with the app installed. When texts start, only parents who tick the “Text me team updates” box get them, and they can reply STOP at any time. You will never pay per text.',
  },
  {
    q: 'What does HoopHero cost for a YMCA program?',
    a: `Coach accounts are ${priceLabel()} after a ${TRIAL_DAYS}-day free trial, no credit card needed to start. One flat fee covers ${INCLUDED}. Parent accounts are always free.`,
  },
  {
    q: 'Can I use HoopHero for more than one team?',
    a: 'Yes. One coach login runs as many programs as you coach. Add a program from the switcher at the top of your coach sidebar, each with its own roster, schedule, families and branch, and change between them in one click.',
  },
  {
    q: 'What if parents do not answer the RSVP?',
    a: 'HoopHero chases them for you. Families who have not answered hear again 72 hours before each game and 24 hours before, by email and as a notification on phones with the app installed (and by text for parents who opt in, once texting starts), until they tap Going, Maybe or Not going.',
  },
  {
    q: 'Can family who cannot make the game follow the score?',
    a: 'Yes. You keep score from the schedule during the game and every game has a scoreboard link you can share. Anyone with the link sees the score update live, no account needed. Mark the game final and the result and top scorers go out to every family.',
  },
  {
    q: 'Is there a team chat, and can I share photos?',
    a: 'Yes to both. Every program has one team chat with read receipts; you can mute a parent who is not keeping it friendly and parents can quiet the notifications for themselves. Team albums hold photos and short videos that only your families can see, and you decide whether parents can upload.',
  },
  {
    q: 'Can parents sign up for snacks and carpools?',
    a: 'Yes. Add what each game needs, snacks, a scorekeeper, someone to bring the balls, and parents claim a slot in one tap. Families with room in the car offer seats and others ask for a ride, right on the schedule.',
  },
  {
    q: 'Can I change the program type later?',
    a: 'Yes. Switch between Paid, Free and Free (YMCA) from your Settings page at any time. Nothing is deleted.',
  },
];

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const FEATURES = [
  { key: 'roster', size: 'third', title: 'Roster & Families', desc: 'Parents register with your program code and add their own children, with jersey numbers, positions and an emergency contact. You stop being data entry.' },
  { key: 'schedule', size: 'third', title: 'Schedule, RSVP & Attendance', desc: 'Add games and practices, parents RSVP in one tap per child, nudge the no-replies, take attendance at tip-off, record the score.' },
  { key: 'messaging', size: 'third', title: 'Two-Way Messaging', desc: 'Message the whole team or a single family. Parents can reply in the app or by email, and every reply comes back to you. Text messages are coming soon.' },
  { key: 'branch', size: 'half', tone: 'orange', title: 'Your YMCA branch, front and center', desc: 'Pick your state, then your branch, from the built-in directory. The branch name shows in your coach header and at the top of every parent’s portal, so a family with kids in two programs always knows which team this is.' },
  { key: 'reminders', size: 'half', tone: 'navy', title: 'Automatic Game Reminders', desc: 'The day before every game, a reminder goes out on its own, by email and as a phone notification (texts coming soon). Families who have not RSVPed hear again 72 and 24 hours before tip-off until they answer. Cancel or postpone with one note to every family.' },
  { key: 'calendar', size: 'third', title: 'Calendar & Seasons', desc: 'Games, practices and events in a month view, with a private calendar feed for Apple, Google and Outlook. Roll over to a new season and keep the archive.' },
  { key: 'waivers', size: 'third', title: 'Waivers & Emergency Contacts', desc: 'Publish a participation waiver from a built-in template. Parents sign by typing their name, and the roster shows who has not.' },
  { key: 'install', size: 'third', title: 'Installs Like an App', desc: 'Coaches and parents add HoopHero to their home screen. No app store, no updates to chase, works on any phone.' },
  { key: 'scoreboard', size: 'third', title: 'Live Scoreboard', desc: 'Keep score from the schedule by period, with points per player. Share one link and family who could not make it watch the score update live. Mark it final and the top scorers go out to every family.' },
  { key: 'chat', size: 'third', title: 'Team Chat', desc: 'One chat for the coach and every family, with read receipts. Mute a parent who is not keeping it friendly; parents can quiet the notifications without leaving.' },
  { key: 'albums', size: 'third', title: 'Photo & Video Albums', desc: 'Team albums for photos and short videos that only your families can see. You upload from any phone, and parents can too if you let them.' },
  { key: 'signups', size: 'half', tone: 'orange', title: 'Carpool & Volunteer Sign-ups', desc: 'Snacks, scorekeeper, who brings the balls: parents claim a slot in one tap. Families with room in the car offer seats and others ask for a ride, right on the schedule.' },
  { key: 'orgs', size: 'half', title: 'Several Programs, One Login', desc: 'Coach two age groups or every team at your branch under one account. Each program keeps its own roster, schedule and families, and you switch between them in one click.' },
];

const BEFORE = [
  { title: 'Three group texts', desc: 'The game time is in one thread, the gym change in another, and half the parents are in neither.' },
  { title: 'Who is coming Saturday?', desc: 'You count replies by hand on Friday night and still have four kids you are not sure about.' },
  { title: 'The roster lives in your other phone', desc: 'Jersey numbers on a napkin, medical notes in an email you cannot find.' },
  { title: 'Who is bringing snacks?', desc: 'The snack list and the carpool live in a thread nobody can find, and the grandparents ask you for the score at halftime.' },
];

const AFTER = [
  { title: 'One place for the whole team', desc: 'Every family sees the same schedule, the same messages and the same reminders, with your branch name at the top.' },
  { title: 'RSVP you can trust', desc: 'Parents tap Going, Maybe or Not going per child. You see the count before you leave the house.' },
  { title: 'Nothing about money, anywhere', desc: 'A Free (YMCA) program never shows a fee, a Payments page or a paid or unpaid badge, to you or to parents.' },
  { title: 'Snacks, rides and scores, sorted', desc: 'Parents claim a slot or a seat from the schedule, and anyone with the scoreboard link follows the game live.' },
];

const YmcaBasketball = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  useReveal(rootRef);

  return (
    <div className="hh-page" ref={rootRef}>
      <PageMeta
        title="HoopHero for YMCA Basketball Coaches — Rosters, Schedule, RSVP & Messaging"
        description="The youth basketball coach app built for YMCA programs. Rosters, schedule, one-tap RSVP that chases the no-replies, live scoreboard link, team chat, photo albums, snack and carpool sign-ups, two-way messaging and automatic game reminders, with your YMCA branch shown to every parent and nothing about money anywhere. 14-day free trial."
        path={PATH}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(FAQ_SCHEMA)}
        </script>
      </Helmet>

      <nav className="hh-nav" aria-label="Site">
        <a className="hh-nav-logo" href="/" aria-label="HoopHero home">
          <img
            src="/logo-wide.png"
            alt="HoopHero — youth basketball program management software"
            className="hh-nav-logo-img"
            width="1119"
            height="390"
          />
        </a>
        <div className="hh-nav-links">
          <button className="hh-nav-link hh-nav-link--coach" onClick={() => navigate('/coach/login')}>Coach login</button>
          <button className="hh-nav-link hh-nav-link--parent" onClick={() => navigate('/parent/login')}>Parent login</button>
          <button className="hh-nav-cta" onClick={() => navigate(REGISTER_YMCA)}>Start free trial</button>
        </div>
      </nav>

      <main>
      {/* ============================================================
          HERO. Written for the YMCA coach. H1 carries the keyword.
          ============================================================ */}
      <header className="hh-hero">
        <div className="hh-hero-inner">
          <div className="hh-hero-content">
            <h1 className="hh-hero-title">
              {'Run your YMCA basketball program '}
              <span className="hh-hero-accent">without the group-text chaos.</span>
            </h1>
            <p className="hh-hero-sub">
              Rosters, schedule, one-tap RSVP, attendance, team chat, a live scoreboard link, snack and carpool sign-ups and automatic game reminders, with your YMCA branch shown to every parent. Built for youth basketball coaches, and nothing about money anywhere.
            </p>
            <div className="hh-hero-btns">
              <button className="hh-btn hh-btn--primary hh-btn--lg" onClick={() => navigate(REGISTER_YMCA)}>
                Start free trial
              </button>
              <button className="hh-btn hh-btn--ghost-light hh-btn--lg" onClick={() => navigate('/parent/register')}>
                Parents: join your team
              </button>
            </div>
          </div>
          <div className="hh-hero-visual" aria-hidden="false">
            <div className="hh-device">
              <img
                className="hh-device-shot"
                src="/screens/coach-roster-phone.png"
                width={390}
                height={844}
                alt="Coach roster on a phone: players, jersey numbers and positions"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
            </div>
          </div>
        </div>
      </header>

      {/* ============================================================
          EMPATHY. The volunteer coach at a Y.
          ============================================================ */}
      <section className="hh-empathy">
        <div className="hh-empathy-inner" data-reveal>
          <h2 className="hh-display">You volunteered to coach kids. Not to run a phone tree.</h2>
          <p className="hh-lead">
            A YMCA season means a roster that changes every week, a gym that moves, and parents who found out about the game from another parent. None of that is coaching.
          </p>
          <p className="hh-lead">
            HoopHero gives a YMCA program the same tools a travel club pays for, minus every screen about money. Pick Free (YMCA) when you sign up and the app never mentions a fee to you or to your parents.
          </p>
        </div>
      </section>

      {/* ============================================================
          WHAT FREE (YMCA) MEANS. Three facts about the program type.
          ============================================================ */}
      <section className="hh-free-programs">
        <div className="hh-free-inner" data-reveal>
          <div className="hh-free-copy">
            <h2 className="hh-h2">Made for the Y, from sign-up on.</h2>
            <p className="hh-body">
              Free (YMCA) is a program type, not a plan. Choose it on the second step of sign-up, pick your branch, and the whole app fits your program from the first login.
            </p>
          </div>
          <dl className="hh-free-strip">
            <div className="hh-free-item">
              <dt>YMCA directory</dt>
              <dd>Pick your state, then your branch. If yours is not listed, type its name and that is what parents will see.</dd>
            </div>
            <div className="hh-free-item">
              <dt>Your branch in every portal</dt>
              <dd>The branch name sits at the top of your coach view and every parent&rsquo;s portal, so the team is easy to recognize.</dd>
            </div>
            <div className="hh-free-item">
              <dt>Nothing about money</dt>
              <dd>No fee fields, no Payments page, no paid or unpaid badges, coach side or parent side. Switch to Paid later from Settings if you ever need to.</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ============================================================
          THE PLAN. Three steps, one CTA.
          ============================================================ */}
      <section className="hh-plan">
        <div className="hh-plan-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">Set up your Y season in three steps.</h2>
          <ol className="hh-steps">
            <li className="hh-step">
              <span className="hh-step-num" aria-hidden="true">1</span>
              <h3 className="hh-step-title">Start your free trial</h3>
              <p className="hh-step-desc">{`${TRIAL_DAYS} days free, no card required. Choose Free (YMCA) and pick your branch.`}</p>
            </li>
            <li className="hh-step">
              <span className="hh-step-num" aria-hidden="true">2</span>
              <h3 className="hh-step-title">Share your program code</h3>
              <p className="hh-step-desc">Parents register their own kids from the code or join link. Your roster fills itself.</p>
            </li>
            <li className="hh-step">
              <span className="hh-step-num" aria-hidden="true">3</span>
              <h3 className="hh-step-title">Coach the season</h3>
              <p className="hh-step-desc">Schedule, RSVP, attendance, chat, live scores, sign-ups and game reminders, handled. Spend your time on the court.</p>
            </li>
          </ol>
          <div className="hh-plan-cta">
            <button className="hh-btn hh-btn--primary" onClick={() => navigate(REGISTER_YMCA)}>
              Start free trial
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================
          FEATURES. Everything a YMCA program gets. No payment cell.
          ============================================================ */}
      <section className="hh-features">
        <div className="hh-features-inner">
          <div className="hh-features-head" data-reveal>
            <h2 className="hh-h2">Everything a YMCA program needs. Nothing it does not.</h2>
            <p className="hh-body">Built for youth basketball coaches running one team or every team at the branch. Every feature below is included for Free (YMCA) programs.</p>
          </div>
          <ul className="hh-bento">
            {FEATURES.map((f, i) => (
              <li key={f.key} className={`hh-cell hh-cell--${f.size}${f.tone ? ` hh-cell--${f.tone}` : ''}`} data-reveal style={{ '--i': i }}>
                <div className="hh-cell-text">
                  <h3 className="hh-cell-title">{f.title}</h3>
                  <p className="hh-cell-desc">{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================================================
          BEFORE / AFTER for a Y program.
          ============================================================ */}
      <section className="hh-compare">
        <div className="hh-compare-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">What changes when the program is organized.</h2>
          <div className="hh-compare-grid">
            <div className="hh-compare-col hh-compare-col--before">
              <h3 className="hh-compare-head">Without HoopHero</h3>
              <ul>
                {BEFORE.map((b) => (
                  <li key={b.title} className="hh-compare-item">
                    <h4 className="hh-compare-title">{b.title}</h4>
                    <p className="hh-compare-desc">{b.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="hh-compare-col hh-compare-col--after">
              <h3 className="hh-compare-head">With HoopHero</h3>
              <ul>
                {AFTER.map((a) => (
                  <li key={a.title} className="hh-compare-item">
                    <h4 className="hh-compare-title">{a.title}</h4>
                    <p className="hh-compare-desc">{a.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PRICING. Coach account only; the parent side is free and
          described in the FAQ. No payment-tracking bullet on this page.
          ============================================================ */}
      <section className="hh-pricing">
        <div className="hh-pricing-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">One flat price. Parents never pay.</h2>
          <div className="hh-pricing-grid">
            <div className="hh-plan-card hh-plan-card--coach">
              <div className="hh-plan-label">Coach account</div>
              <div className="hh-plan-price">{priceAmount()}<span className="hh-plan-per">/month</span></div>
              <p className="hh-plan-desc">{`One flat fee per coach. Covers ${INCLUDED}.`}</p>
              <ul className="hh-plan-list">
                {[
                  `${TRIAL_DAYS}-day free trial, no card needed`,
                  'Unlimited basketball programs on one login',
                  'Unlimited seasons',
                  'Unlimited families, players and games',
                  'YMCA branch shown to every parent',
                  'Schedule, RSVP and attendance',
                  'Two-way messaging with families',
                  'Automatic game reminders (texts coming soon)',
                  'Waivers and emergency contacts',
                  'Live scoreboard with a shareable link',
                  'Team chat, photo and video albums',
                  'Snack and carpool sign-ups',
                  'Cancel anytime',
                ].map((f) => (
                  <li key={f} className="hh-plan-item">{f}</li>
                ))}
              </ul>
              <button className="hh-btn hh-btn--primary hh-btn--full" onClick={() => navigate(REGISTER_YMCA)}>
                Start free trial
              </button>
              <div className="hh-plan-foot">No credit card required for the trial</div>
            </div>

            <div className="hh-plan-card hh-plan-card--parent">
              <div className="hh-plan-label">Parent account</div>
              <div className="hh-plan-price">{'Free '}<span className="hh-plan-per">forever</span></div>
              <p className="hh-plan-desc">Parent accounts are always free. No subscriptions, nothing to buy.</p>
              <ul className="hh-plan-list">
                {[
                  'Add your own children',
                  'View the full game schedule',
                  'RSVP to games in one tap',
                  'Reply to your coach in the app',
                  'Email, phone notifications and reminders',
                  'Medical info for each child',
                  'Live scores, team chat and albums',
                  'Snack and carpool sign-ups',
                  'Your YMCA branch at the top of the portal',
                ].map((f) => (
                  <li key={f} className="hh-plan-item">{f}</li>
                ))}
              </ul>
              <button className="hh-btn hh-btn--navy hh-btn--full" onClick={() => navigate('/parent/register')}>
                Join your team
              </button>
              <div className="hh-plan-foot">Ask your coach for the program code</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ. Same list feeds the JSON-LD above.
          ============================================================ */}
      <section className="hh-faq">
        <div className="hh-faq-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">Questions from YMCA coaches</h2>
          <div className="hh-faq-list">
            {FAQS.map((f) => (
              <details key={f.q} className="hh-faq-item">
                <summary className="hh-faq-q">{f.q}</summary>
                <p className="hh-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
          ============================================================ */}
      <section className="hh-final">
        <div className="hh-final-inner" data-reveal>
          <h2 className="hh-display hh-display--center">Ready for a calmer season at the Y?</h2>
          <p className="hh-lead hh-lead--center">
            {`Start your ${TRIAL_DAYS}-day free trial in under two minutes. No credit card. ${priceLabel()} after, cancel anytime.`}
          </p>
          <button className="hh-btn hh-btn--primary hh-btn--lg" onClick={() => navigate(REGISTER_YMCA)}>
            Start free trial
          </button>
        </div>
      </section>
      </main>

      <footer className="hh-footer">
        <div className="hh-footer-inner">
          <img
            src="/logo-wide.png"
            alt="HoopHero — youth basketball program management software"
            className="hh-footer-logo-img"
            width="1119"
            height="390"
            loading="lazy"
          />
          <div className="hh-footer-links">
            <button className="hh-footer-link" onClick={() => navigate('/')}>Home</button>
            <button className="hh-footer-link" onClick={() => navigate('/coach/login')}>Coach login</button>
            <button className="hh-footer-link" onClick={() => navigate('/parent/login')}>Parent login</button>
            <button className="hh-footer-link" onClick={() => navigate(REGISTER_YMCA)}>Start free trial</button>
            <button className="hh-footer-link" onClick={() => navigate('/getting-started')}>Getting started</button>
            <button className="hh-footer-link" onClick={() => navigate('/terms')}>Terms</button>
            <button className="hh-footer-link" onClick={() => navigate('/privacy')}>Privacy</button>
            <button className="hh-footer-link" onClick={() => navigate('/sms')}>Text messages</button>
          </div>
          <div className="hh-footer-note">© 2026 Clarity Growth Pro LLC. HoopHero is independent software and is not affiliated with or endorsed by the YMCA.</div>
        </div>
      </footer>
    </div>
  );
};

export default YmcaBasketball;
